<template>
    <div class="layout-topbar">
        <div class="layout-topbar-left">
            <a href="#" class="topbar-menu-button" @click="onMenuButtonClick($event)">
                <i class="pi pi-bars"></i>
            </a>

            <a href="/" class="logo">
                <img id="logo-id" :src="'layout/images/logo-' + colorScheme + '.png'" />
            </a>

            <a href="/">
                <img id="app-name-id" :src="'layout/images/appname-' + colorScheme + '.png'" class="app-name" />
            </a>
        </div>
        <AppMenu
            id="app-topbar"
            :userRole="userRole"
            :model="items"
            :menuMode="menuMode"
            :colorScheme="colorScheme"
            :menuActive="menuActive"
            :pinActive="false"
            :activeInlineProfile="activeInlineProfile"
            @sidebar-mouse-over="onSidebarMouseOver"
            @sidebar-mouse-leave="onSidebarMouseLeave"
            @toggle-menu="onToggleMenu"
            @change-inlinemenu="onChangeActiveInlineMenu"
            @menu-click="onMenuClick"
            @root-menuitem-click="onRootMenuItemClick"
            @menuitem-click="onMenuItemClick"
        />
        <div class="layout-topbar-right">
            <ul class="layout-topbar-right-items">
                <li
                    id="profile"
                    class="profile-item p-avatar-circle p-avatar-xl"
                    :class="{ 'active-topmenuitem': topbarMenuActive }"
                >
                    <a href="#" @click="onTopbarItemClick($event, 'profile')">
                        <img :src="profileAvatar" />
                    </a>

                    <ul class="fadeInDown">
                        <li role="menuitem">
                            <a href="#" @click="$router.push('settings')">
                                <i class="pi pi-fw pi-cog"></i>
                                <span>Settings</span>
                            </a>
                        </li>
                        <li role="menuitem">
                            <a href="#" @click="this.signOut">
                                <i class="pi pi-fw pi-sign-out"></i>
                                <span>Logout</span>
                            </a>
                        </li>
                    </ul>
                </li>
                <li style="margin-right: 10px"></li>
                <li>
                    <ClientSelector
                        :selectedClient="selectedClient"
                        :clientOptions="clientOptions"
                        @handleSelectedClient="handleSelectedClient($event)"
                    />
                </li>
                <li style="margin-right: 10px"></li>
                <li>
                    <DatabaseSelector
                        :selectedDatabase="selectedDatabase"
                        :databaseOptions="databaseOptions"
                        @handleSelectedDatabase="handleDatabaseSelection($event)"
                        :disabled="disableDatabaseSelector"
                    />
                </li>
                <template v-if="apicalIngestEnabled && canAccessApical">
                    <li
                        v-if="isApicalSignedIn"
                        class="profile-item"
                        :class="{ 'active-topmenuitem': topbarApicalMenuActive }"
                        style="margin-right: 10px"
                    >
                        <a
                            href="#"
                            @click="onTopbarApicalItemClick($event)"
                            class="flex align-items-center gap-2 text-sm"
                            style="white-space: nowrap; width: auto; height: 36px; padding: 0 0.75rem"
                        >
                            <img
                                v-if="apicalAvatarUrl"
                                :src="apicalAvatarUrl"
                                style="width: 24px; height: 24px; border-radius: 50%"
                            />
                            <span>Apical: {{ apicalShortName }}</span>
                        </a>
                        <ul class="fadeInDown">
                            <li class="px-3 py-2 text-color-secondary text-sm" style="pointer-events: none">
                                {{ apicalEmail }}
                            </li>
                            <li role="menuitem">
                                <a href="#" @click.prevent="clickApicalSignOut">
                                    <i class="pi pi-fw pi-sign-out"></i>
                                    <span>Sign out of Apical</span>
                                </a>
                            </li>
                        </ul>
                    </li>
                    <li v-else style="margin-right: 10px">
                        <Button label="Sign in to Apical" size="small" outlined @click="clickApicalSignIn" />
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<script>
import ClientSelector from '@/components/listGeneration/ClientSelector.vue';
import DatabaseSelector from '@/components/listGeneration/DatabaseSelector.vue';
import { validRoles, replaceHtmlElementShared } from '@/store/sharedFunctions';
import AppMenu from './AppMenu.vue';
import { apicalOAuth } from '@/store/apicalOAuth';
export default {
    emits: [
        'menu-click',
        'menuitem-click',
        'root-menuitem-click',
        'menubutton-click',
        'sidebar-mouse-over',
        'sidebar-mouse-leave',
        'toggle-menu',
        'change-inlinemenu',
        'topbar-item-click',
        'topbar-apical-item-click',
        'handle-selected-client',
        'handle-selected-database',
    ],
    data() {
        return {
            activeTopbarItem: null,
            uid: '',
            disableDatabaseSelector: true,
            apicalIngestEnabled: process.env.VUE_APP_APICAL_INGESTION_ENABLED === 'true',
            // OIDC profile claims (given_name, family_name, email, picture) and
            // signed-in state, refreshed from the OAuth client.
            apicalUser: null,
            isApicalSignedIn: false,
        };
    },
    props: {
        items: Array,
        menuMode: String,
        colorScheme: String,
        topbarMenuActive: {
            type: Boolean,
            default: false,
        },
        menuActive: {
            type: Boolean,
            default: false,
        },
        activeInlineProfile: {
            type: Boolean,
            default: false,
        },
        selectedClient: Object,
        userUid: String,
        userRole: String,
        clientOptions: Array,
        selectedDatabase: Object,
        databaseOptions: Array,
        topbarApicalMenuActive: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        onMenuClick(event) {
            this.$emit('menu-click', event);
        },
        onMenuItemClick(event) {
            this.$emit('menuitem-click', event);
        },
        onRootMenuItemClick(event) {
            this.$emit('root-menuitem-click', event);
        },
        onMenuButtonClick(event) {
            this.$emit('menubutton-click', event);
        },
        onTopbarItemClick(event, item) {
            this.$emit('topbar-item-click', event, item);
            event.preventDefault();
        },
        onSidebarMouseOver() {
            this.$emit('sidebar-mouse-over');
        },
        onSidebarMouseLeave() {
            this.$emit('sidebar-mouse-leave');
        },
        onToggleMenu(event) {
            this.$emit('toggle-menu', event);
        },
        onChangeActiveInlineMenu() {
            this.$emit('change-inlinemenu');
        },
        handleSelectedClient(event) {
            this.$emit('handle-selected-client', event);
        },
        handleDatabaseSelection(event) {
            this.$emit('handle-selected-database', event);
        },
        setDatabaseSelectorAccess(userRole) {
            this.disableDatabaseSelector = !validRoles('database-selector').includes(userRole);
        },
        signOut() {
            replaceHtmlElementShared(
                'theme-css',
                'layout-css',
                'theme/green/theme-',
                'layout/css/green/layout-',
                'dark',
                '.css',
                false,
                false
            );
            this.$store.dispatch('signOut');
        },
        onTopbarApicalItemClick(event) {
            this.$emit('topbar-apical-item-click', event);
            event.preventDefault();
        },
        async refreshApicalAuth() {
            try {
                const user = await apicalOAuth().getUser();
                this.apicalUser = user ? user.profile : null;
                this.isApicalSignedIn = !!user && !user.expired;
            } catch (error) {
                // OAuth not configured / storage unavailable — treat as signed out
                // rather than breaking the top bar.
                console.error('Could not read Apical auth state', error);
                this.apicalUser = null;
                this.isApicalSignedIn = false;
            }
        },
        async clickApicalSignIn() {
            try {
                await apicalOAuth().signIn();
            } catch (error) {
                console.error('Apical sign-in failed', error);
            }
            await this.refreshApicalAuth();
        },
        async clickApicalSignOut() {
            await apicalOAuth().signOut();
            await this.refreshApicalAuth();
        },
    },
    computed: {
        profileAvatar() {
            return this.$store.state.dbcalls.userDoc ? this.$store.state.dbcalls.userDoc.preference.avatar : '';
        },
        apicalShortName() {
            const fn = this.apicalUser?.given_name;
            const li = this.apicalUser?.family_name?.[0];
            if (fn && li) return `${fn} ${li}`;
            return fn || this.apicalUser?.name || this.apicalUser?.email || '';
        },
        apicalEmail() {
            return this.apicalUser?.email || '';
        },
        apicalAvatarUrl() {
            return this.apicalUser?.picture || '';
        },
        canAccessApical() {
            return validRoles('apical').includes(this.userRole);
        },
    },
    watch: {
        userRole: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.setDatabaseSelectorAccess(newVal);
                }
            },
        },
    },
    components: {
        ClientSelector,
        DatabaseSelector,
        AppMenu,
    },
    async mounted() {
        this.setDatabaseSelectorAccess(this.userRole);
        await this.refreshApicalAuth();
    },
};
</script>
